import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../context/AuthContext';
import './LabSelector.css';

interface Lab {
  id: number;
  name: string;
  cpsc_id?: string;
  city?: string;
  state_province?: string;
  country?: string;
  source: 'user' | 'certified';
}

interface LabSelectorProps {
  selectedLab: Lab | null;
  onLabSelect: (lab: Lab | null) => void;
  placeholder?: string;
  className?: string;
  certificateType?: 'GCC' | 'CPC' | null;
}

const LabSelector = ({
  selectedLab,
  onLabSelect,
  placeholder = "Search for a test lab...",
  className = "",
  certificateType = null
}: LabSelectorProps) => {
  const { user } = useAuth();
  const [labs, setLabs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredLabs, setFilteredLabs] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Fetch all labs on component mount
  useEffect(() => {
    fetchLabs();
  }, [user?.id]);

  // Sync searchTerm with selectedLab when it changes from outside
  useEffect(() => {
    if (selectedLab) {
      setSearchTerm(selectedLab.name);
    } else {
      setSearchTerm('');
    }
  }, [selectedLab]);

  // Filter labs based on search term and certificate type restrictions
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredLabs([]);
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    let filtered = labs.filter((lab: Lab) => {
      // First apply search filtering
      const matchesSearch = (
        lab.name?.toLowerCase().includes(searchLower) ||
        lab.cpsc_id?.toLowerCase().includes(searchLower) ||
        lab.city?.toLowerCase().includes(searchLower) ||
        lab.state_province?.toLowerCase().includes(searchLower) ||
        lab.country?.toLowerCase().includes(searchLower)
      );

      if (!matchesSearch) return false;

      // Apply certificate type restrictions
      if (certificateType === 'CPC') {
        // For CPC products, only allow certified labs
        return lab.source === 'certified';
      } else if (certificateType === 'GCC') {
        // For GCC products, allow any lab (both user and certified)
        return true;
      }

      // If no certificate type specified, allow all labs
      return true;
    });

    // Limit results to 20 for performance
    setFilteredLabs(filtered.slice(0, 20));
  }, [searchTerm, labs, certificateType]);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchLabs = async () => {
    try {
      setLoading(true);
      
      // Fetch user's test labs
      const { data: userLabs, error: userError } = await supabase
        .from('test_labs')
        .select('id, name, cpsc_id, city, state_province, country')
        .eq('auth_id', user?.id);

      // Fetch certified labs
      const { data: certifiedLabs, error: certifiedError } = await supabase
        .from('certified_labs')
        .select('id, name, cpsc_id, city, state_province, country');

      if (userError) throw userError;
      if (certifiedError) throw certifiedError;

      // Combine and mark source
      const combinedLabs = [
        ...(userLabs || []).map((lab: any) => ({ ...lab, source: 'user' as const })),
        ...(certifiedLabs || []).map((lab: any) => ({ ...lab, source: 'certified' as const }))
      ];

      // Sort by name for better UX
      combinedLabs.sort((a, b) => a.name.localeCompare(b.name));

      setLabs(combinedLabs);
    } catch (err) {
      console.error('Error fetching labs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
    setIsOpen(true);
    
    // If input is cleared, clear selection
    if (!value.trim()) {
      onLabSelect(null);
    } else if (selectedLab && value !== selectedLab.name) {
      // If user is typing something different from selected lab, clear selection
      onLabSelect(null);
    }
  };

  const handleLabSelect = (lab: Lab) => {
    onLabSelect(lab);
    setSearchTerm(lab.name); // Show lab name in input
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const formatLabDisplay = (lab: Lab) => {
    const parts = [];
    
    // Lab name (truncated if too long)
    const name = lab.name.length > 25 ? `${lab.name.substring(0, 25)}...` : lab.name;
    parts.push(name);
    
    // CPSC ID if available
    if (lab.cpsc_id) {
      parts.push(`(${lab.cpsc_id})`);
    }
    
    // Location
    const location = [];
    if (lab.city) location.push(lab.city);
    if (lab.state_province) {
      // Abbreviate state/province if longer than 3 chars
      const state = lab.state_province.length > 3 ? lab.state_province.substring(0, 2).toUpperCase() : lab.state_province;
      location.push(state);
    }
    if (lab.country) location.push(lab.country);
    
    if (location.length > 0) {
      parts.push(`- ${location.join(', ')}`);
    }
    
    return parts.join(' ');
  };

  // Set display value for input - use searchTerm when user is actively searching
  const displayValue = searchTerm;

  return (
    <div className={`lab-selector ${className}`}>
      <input
        ref={inputRef}
        type="text"
        className="lab-selector-input"
        placeholder={placeholder}
        value={displayValue}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={handleInputFocus}
      />
      
      {isOpen && (
        <div ref={dropdownRef} className="lab-selector-dropdown">
          {loading ? (
            <div className="lab-selector-loading">Loading labs...</div>
          ) : filteredLabs.length > 0 ? (
            <>
              {filteredLabs.map((lab: Lab) => (
                <div
                  key={`${lab.source}-${lab.id}`}
                  className="lab-selector-option"
                  onClick={() => handleLabSelect(lab)}
                >
                  <div className="lab-option-main">
                    {formatLabDisplay(lab)}
                  </div>
                  {lab.source === 'user' && (
                    <div className="lab-option-badge">Your Lab</div>
                  )}
                </div>
              ))}
              {labs.length > 20 && (
                <div className="lab-selector-more">
                  Showing first 20 results. Keep typing to narrow down...
                </div>
              )}
            </>
          ) : searchTerm.trim() ? (
            <div className="lab-selector-no-results">
              No labs found matching "{searchTerm}"
            </div>
          ) : (
            <div className="lab-selector-prompt">
              Enter text in the lab name, lab address, and/or CPSC ID...
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LabSelector;